/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale off -->*/
/*<!-- vale on -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Holdout Group Logs", description = "Holdout Group Logs tracking allows organizations to accurately understand which rows are included in which Split from specific Audiences. Marketers can use this information to track which rows were not synced to a destination to perform campaign analysis.", lastUpdatedDate = new Date(1721347200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Holdout Group Logs",
        description: "Holdout Group Logs tracking allows organizations to accurately understand which rows are included in which Split from specific Audiences. Marketers can use this information to track which rows were not synced to a destination to perform campaign analysis.",
        lastUpdatedDate: "Fri Jul 19 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Table definition",
          "depth": 1,
          "id": "table-definition"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "What are the limitations in Redshift?",
          "depth": 1,
          "id": "what-are-the-limitations-in-redshift"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      code: "code",
      a: "a",
      h2: "h2",
      strong: "strong",
      img: "img",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      em: "em"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Holdout Group Logs requires ", _jsx(_components.code, {
            children: "WRITE"
          }), " permissions to your data warehouse.\nBecause of this, Hightouch only records rows of audiences built off sources that have the ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "Lightning sync engine"
          }), " enabled."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Holdout Group Logs tracking allows organizations and marketers to accurately understand which rows were\nnot synced to a destination and included in other split groups. This can be useful when wanting to do analysis on\ncampaigns where you need to understand exactly which rows were included in which split groups at the time of a given sync."
      }), "\n", _jsx(_components.p, {
        children: "A common use case is when syncing a Treatment vs Holdout group for an audience and wanting the ability to accurately\nknow which rows were synced to the destination and which were part of the holdout group."
      }), "\n", _jsxs(_components.p, {
        children: ["Only rows that are ", _jsx(_components.strong, {
          children: "not"
        }), " included in the sync split group are written to the ", _jsx(_components.code, {
          children: "hightouch_audit.audience_holdout"
        }), " table,\nand other synced rows are still written to the ", _jsx(_components.code, {
          children: "hightouch_audit.sync_changelog"
        }), " table. You will need to join both tables\nto get a full picture of what is synced and not synced."]
      }), "\n", _jsxs(_components.p, {
        children: ["This feature requires the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine",
          children: "Lightning sync engine"
        }), " to be enabled."]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information on audiences and splits, go to the ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/customer-studio/usage",
          children: "audiences"
        }), " or ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/customer-studio/splits",
          children: "splits"
        }), " pages."]
      }), "\n", _jsxs(_components.p, {
        children: ["This feature is still in Beta. Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm interested in holdout group logs.",
          linkText: "reach out"
        }), " if you're interested in Holdout Group Logs!"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/enable-audience-holdout.png",
          alt: "Enable holdouts"
        })
      }), "\n", _jsx(_components.h3, {
        id: "table-definition",
        children: "Table definition"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "hightouch_audit.audience_holdout"
        }), " table is an ", _jsx(_components.strong, {
          children: "append-only"
        }), " log. If the same row is included in multiple sync runs, it has multiple entries in this table."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "COLUMN"
            }), _jsx(_components.th, {
              children: "DESCRIPTION"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the sync"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_run_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the sync run"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "model_id"
              })
            }), _jsx(_components.td, {
              children: "The ID of the model attached to the sync. This is the same as the audience ID."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "The timestamp the sync occured."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_id"
              })
            }), _jsx(_components.td, {
              children: "The value of the row's primary key as defined from the model"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "fields"
              })
            }), _jsx(_components.td, {
              children: "A JSON object of the raw data from the model that is synced into the destination. Note that this is the raw data from the warehouse, not the payload that Hightouch sent to the destination. This column has limitations in Redshift (see FAQ for details, below)."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "split_group"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.a, {
                href: "/customer-studio/splits",
                children: "split group"
              }), " name."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "what-are-the-limitations-in-redshift",
        children: "What are the limitations in Redshift?"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["Because ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/redshift/latest/dg/r_Character_types.html#r_Character_types-storage-and-ranges",
          children: "Redshift does not support strings longer than 65,535 bytes"
        }), ",\nit is not possible to store JSON representations of arbitrary length in the ", _jsx(_components.code, {
          children: "audience_holdout"
        }), " table. Hightouch attempts\nto store model data in the ", _jsx(_components.code, {
          children: "fields"
        }), ", but if the model contains either long strings or a large number of fields,\nsome fields may be truncated. Truncated values in the ", _jsx(_components.code, {
          children: "audience_holdout"
        }), " table do ", _jsx(_components.em, {
          children: "not"
        }), " mean that values were truncated\nwhen Hightouch synced data to the destination; it's purely a limitation of the warehouse logs."]
      }), "\n"]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
